import { lazy, Suspense, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Celebration } from "@/components/ui/celebration";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/components/ThemeProvider";
import { queryClient } from "@/lib/queryClient";
import { usePageTracking } from "@/hooks/usePageTracking";
import { setTrackingUserId, initScrollTracking, stopTracking } from "@/services/eventTrackingService";
import { RouteProgress } from "@/components/RouteProgress";
import { prefetchHotRoutes } from "@/lib/routePrefetch";



// Lazy load all pages
const Landing = lazy(() => import("./pages/Landing"));
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const SignUp = lazy(() => import("./pages/SignUp"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Services = lazy(() => import("./pages/Services"));
const Billing = lazy(() => import("./pages/Billing"));
const Templates = lazy(() => import("./pages/Templates"));
const ResumeExamples = lazy(() => import("./pages/ResumeExamples"));
const CoverLetterTemplates = lazy(() => import("./pages/CoverLetterTemplates"));
const CareerBlog = lazy(() => import("./pages/CareerBlog"));
const Resources = lazy(() => import("./pages/Resources"));
const Survey = lazy(() => import("./pages/Survey"));
const ResourceGuidePage = lazy(() => import("./pages/ResourceGuide"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ProductHuntLaunch = lazy(() => import("./pages/ProductHuntLaunch"));
const AIResumeBuilder = lazy(() => import("./pages/AIResumeBuilder"));
const ATSResumeChecker = lazy(() => import("./pages/ATSResumeChecker"));
const ResumeBuilderIndia = lazy(() => import("./pages/ResumeBuilderIndia"));
const ResumeBuilderEngineers = lazy(() => import("./pages/ResumeBuilderEngineers"));
const ResumeBuilderFreshers = lazy(() => import("./pages/ResumeBuilderFreshers"));
const FreeResumeMaker = lazy(() => import("./pages/FreeResumeMaker"));
const ResumeFormatPDF = lazy(() => import("./pages/ResumeFormatPDF"));
const RecruiterSignUp = lazy(() => import("./pages/RecruiterSignUp"));
const ForRecruiters = lazy(() => import("./pages/ForRecruiters"));
const RoleKeywordsIndex = lazy(() => import("./pages/RoleKeywordsIndex"));
const RoleKeywordsPage = lazy(() => import("./pages/RoleKeywordsPage"));
const RecruiterLogin = lazy(() => import("./pages/RecruiterLogin"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const RecruiterDashboard = lazy(() => import("./pages/RecruiterDashboard"));
const Champions = lazy(() => import("./pages/Champions"));
const UniversityDashboard = lazy(() => import("./pages/UniversityDashboard"));
const OracleSupport = lazy(() => import("./pages/OracleSupport"));
const LayoffSupport = lazy(() => import("./pages/LayoffSupport"));
const GCCResume = lazy(() => import("./pages/GCCResume"));
const MBAResume = lazy(() => import("./pages/MBAResume"));
const GlobalResume = lazy(() => import("./pages/GlobalResume"));

function PageTracker() {
  usePageTracking();
  const { user } = useAuth();

  useEffect(() => {
    setTrackingUserId(user?.id ?? null);
    if (user) {
      const cleanup = initScrollTracking();
      return () => { cleanup(); stopTracking(); };
    }
  }, [user]);

  useEffect(() => {
    prefetchHotRoutes();
  }, []);

  return null;
}

function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function RouteFallback() {
  // Lightweight top progress bar — keeps previous page visible instead of a full-screen spinner
  return <RouteProgress active />;
}



function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) return <LoadingSpinner />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Celebration />
          <Sonner />
          <BrowserRouter>
            <PageTracker />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Index />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/signup"
                  element={
                    <PublicRoute>
                      <SignUp />
                    </PublicRoute>
                  }
                />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/admin" element={<AdminLogin />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/templates" element={<Templates />} />
                <Route path="/resume-examples" element={<ResumeExamples />} />
                <Route path="/cover-letter-templates" element={<CoverLetterTemplates />} />
                <Route path="/career-blog" element={<CareerBlog />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/survey/:token" element={<Survey />} />
                <Route path="/resources/:slug" element={<ResourceGuidePage />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/launch" element={<ProductHuntLaunch />} />
                <Route path="/ai-resume-builder" element={<AIResumeBuilder />} />
                <Route path="/ats-resume-checker" element={<ATSResumeChecker />} />
                <Route path="/resume-builder-india" element={<ResumeBuilderIndia />} />
                <Route path="/resume-builder-for-engineers" element={<ResumeBuilderEngineers />} />
                <Route path="/resume-builder-for-freshers" element={<ResumeBuilderFreshers />} />
                <Route path="/free-resume-maker" element={<FreeResumeMaker />} />
                <Route path="/resume-format-pdf" element={<ResumeFormatPDF />} />
                <Route path="/services" element={<Services />} />
                {/* The AI builder is free; what's for sale now are the expert services */}
                <Route path="/pricing" element={<Navigate to="/services" replace />} />
                <Route path="/recruiters" element={<ForRecruiters />} />
                <Route path="/resume-keywords" element={<RoleKeywordsIndex />} />
                <Route path="/resume-keywords/:slug" element={<RoleKeywordsPage />} />
                <Route path="/recruiter/signup" element={<RecruiterSignUp />} />
                <Route path="/recruiter/login" element={<RecruiterLogin />} />
                <Route path="/recruiter/dashboard" element={<RecruiterDashboard />} />
                <Route path="/university/dashboard" element={<UniversityDashboard />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/champions" element={<Champions />} />
                <Route path="/oracle-support" element={<OracleSupport />} />
                <Route path="/layoff-support" element={<LayoffSupport />} />
                <Route path="/gcc-resume" element={<GCCResume />} />
                <Route path="/mba-resume" element={<MBAResume />} />
                <Route path="/global-resume" element={<GlobalResume />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
